import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Blueprint, ArrowRight, UserCircle } from '@phosphor-icons/react';
import api from '../lib/api';

export default function LoginPage() {
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [demoEmail, setDemoEmail] = useState(null);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/auth/demo-credentials').then(res => {
      if (res.data.has_demo) setDemoEmail(res.data.email);
    }).catch(() => {});
  }, []);

  const fillDemo = () => {
    setEmail('demo@dhr-sa.com');
    setPassword('Demo@2024');
    setIsRegister(false);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isRegister) {
        await register(email, password, name);
      } else {
        await login(email, password);
      }
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F7F7] flex" data-testid="login-page">
      {/* Left - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 mb-12">
            <Blueprint size={32} weight="bold" className="text-[#FF5A00]" />
            <span className="font-sans text-2xl font-black tracking-tight">DHR</span>
          </Link>

          <h1 className="font-sans text-4xl font-black tracking-tight mb-2">
            {isRegister ? 'Create Account' : 'Welcome Back'}
          </h1>
          <p className="font-mono text-sm text-[#5E5E5E] mb-8">
            {isRegister ? 'Start analyzing construction drawings with AI' : 'Sign in to continue to your dashboard'}
          </p>

          {/* Demo Account */}
          {demoEmail && !isRegister && (
            <button
              onClick={fillDemo}
              data-testid="demo-login-btn"
              className="w-full mb-6 flex items-center gap-3 p-4 bg-[#F7F7F7] border border-dashed border-black/20 hover:border-[#FF5A00] hover:bg-[#FF5A00]/5 transition-colors text-left"
            >
              <UserCircle size={32} weight="regular" className="text-[#FF5A00] shrink-0" />
              <div>
                <p className="font-mono text-xs font-bold uppercase tracking-wide">Try Demo Account</p>
                <p className="font-mono text-[10px] text-[#8F8F8F] mt-0.5">Click to auto-fill demo credentials</p>
              </div>
            </button>
          )}

          {error && (
            <div className="mb-6 p-4 bg-[#FF2B2B]/10 border border-[#FF2B2B]/30 font-mono text-sm text-[#FF2B2B]" data-testid="auth-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {isRegister && (
              <div>
                <label className="block font-mono text-xs uppercase tracking-wider text-[#5E5E5E] mb-2">Full Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required={isRegister}
                  data-testid="register-name-input"
                  className="w-full bg-transparent border-b border-black/30 focus:border-black px-0 py-3 font-mono text-sm focus:outline-none focus:ring-0 transition-colors"
                  placeholder="Enter your name"
                />
              </div>
            )}

            <div>
              <label className="block font-mono text-xs uppercase tracking-wider text-[#5E5E5E] mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="w-full bg-transparent border-b border-black/30 focus:border-black px-0 py-3 font-mono text-sm focus:outline-none focus:ring-0 transition-colors"
                placeholder="name@company.com"
              />
            </div>

            <div>
              <label className="block font-mono text-xs uppercase tracking-wider text-[#5E5E5E] mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="w-full bg-transparent border-b border-black/30 focus:border-black px-0 py-3 font-mono text-sm focus:outline-none focus:ring-0 transition-colors"
                placeholder="Enter password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full bg-[#FF5A00] text-white py-3 px-6 font-mono text-sm uppercase tracking-wide hover:bg-[#E04E00] transition-all duration-150 hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none shadow-[4px_4px_0px_rgba(0,0,0,1)] disabled:opacity-50"
            >
              {loading ? 'Processing...' : (isRegister ? 'Create Account' : 'Sign In')}
              {!loading && <ArrowRight size={16} weight="bold" className="inline ml-2" />}
            </button>
          </form>

          <p className="mt-8 font-mono text-sm text-[#5E5E5E]">
            {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button
              onClick={() => { setIsRegister(!isRegister); setError(''); }}
              data-testid="toggle-auth-mode"
              className="text-[#FF5A00] hover:underline font-medium"
            >
              {isRegister ? 'Sign In' : 'Create Account'}
            </button>
          </p>
        </div>
      </div>

      {/* Right - Image */}
      <div className="hidden lg:block w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1503387837-b154d5074bd2"
          alt="Architecture"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute bottom-12 left-12 right-12">
          <p className="font-sans text-3xl font-black text-white tracking-tight leading-tight">
            Transform Construction Drawings Into Precise BOQ
          </p>
          <p className="font-mono text-sm text-white/70 mt-4">
            AI-powered analysis for walls, doors, windows, dimensions and more
          </p>
        </div>
      </div>
    </div>
  );
}
